import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MetricCard } from "@/components/analytics/MetricCard";
import { TrendChart } from "@/components/analytics/TrendChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { get } from "@/lib/api";
import { useArtistScope } from "@/lib/artistContext";
import { getStoredSpotifyArtistMap, resolveSpotifyArtistId, subscribeSpotifyArtistMap } from "@/lib/spotify";

type SpotifyArtist = {
  id: string;
  name: string;
  followers?: { total: number };
  popularity?: number;
  genres?: string[];
  source?: string;
  snapshot_at?: string;
};

type SpotifyTrack = {
  id?: string;
  name: string;
  popularity?: number;
  album?: { release_date?: string };
  source?: string;
  snapshot_at?: string;
};

type SpotifyAlbum = {
  id?: string;
  name: string;
  release_date?: string;
  total_tracks?: number;
  album_type?: string;
  label?: string;
  release_date_precision?: string;
  available_markets?: string[];
  external_urls?: {
    spotify?: string;
  };
};

type RelatedArtist = {
  id: string;
  name: string;
  genres?: string[];
  popularity?: number;
};

type PopularitySnapshot = {
  snapshot_at?: string;
  popularity?: number;
  followers?: number;
  source?: string;
};

const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});


export default function MusicAnalyticsPage() {
  const { artists, selectedArtistId } = useArtistScope();
  const [spotifyArtistMap, setSpotifyArtistMap] = useState<Record<string, string>>(() => getStoredSpotifyArtistMap());
  const [syncState, setSyncState] = useState<{ status: string; artist_id?: string; timestamp?: string } | null>(null);
  const [artist, setArtist] = useState<SpotifyArtist | null>(null);
  const [topTracks, setTopTracks] = useState<SpotifyTrack[]>([]);
  const [popularityHistory, setPopularityHistory] = useState<PopularitySnapshot[]>([]);
  const [artistAlbums, setArtistAlbums] = useState<SpotifyAlbum[]>([]);
  const [relatedArtists, setRelatedArtists] = useState<RelatedArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheNotice, setCacheNotice] = useState<string | null>(null);
  const selectedArtistName = useMemo(() => {
    if (!selectedArtistId) return null;
    const entry = artists.find((candidate) => candidate.id === selectedArtistId);
    return (entry?.name ?? selectedArtistId)?.trim() || null;
  }, [artists, selectedArtistId]);
  const spotifyArtistId = useMemo(
    () => resolveSpotifyArtistId(selectedArtistId, spotifyArtistMap),
    [selectedArtistId, spotifyArtistMap],
  );
  const artistSelectionNotice = useMemo<ReactNode>(() => {
    if (!selectedArtistId) {
      return "Select an artist to load Spotify analytics.";
    }
    if (!spotifyArtistId) {
      return (
        <>
          No Spotify artist ID is linked for {selectedArtistName ?? selectedArtistId}.{" "}
          <Link to="/settings" className="underline decoration-dashed decoration-primary/60 text-primary hover:text-foreground">
            Go to Account &amp; Settings → Link Spotify
          </Link>{" "}
          to connect it.
        </>
      );
    }
    return null;
  }, [selectedArtistId, spotifyArtistId, selectedArtistName]);
  const displayArtistName = artist?.name ?? selectedArtistName ?? selectedArtistId ?? "Spotify Artist";

  useEffect(() => {
    let mounted = true;
    fetch("/api/spotify/sync/state")
      .then((res) => res.json())
      .then((data) => {
        if (mounted) setSyncState(data);
      })
      .catch(() => {
        if (mounted) setSyncState({ status: "unknown" });
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => subscribeSpotifyArtistMap((map) => setSpotifyArtistMap(map)), []);

  useEffect(() => {
    let cancelled = false;

    async function loadSpotifyAnalytics() {
      if (!spotifyArtistId) {
        setArtist(null);
        setTopTracks([]);
        setPopularityHistory([]);
        setArtistAlbums([]);
        setRelatedArtists([]);
        setCacheNotice(null);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setCacheNotice(null);
      setPopularityHistory([]);
      setTopTracks([]);
      setArtistAlbums([]);
      setRelatedArtists([]);
      try {
        const cacheSources: string[] = [];
        const snapshotDates: Date[] = [];
        const recordSnapshot = (input?: string) => {
          if (!input) return;
          const parsed = new Date(input);
          if (!Number.isNaN(parsed.getTime())) {
            snapshotDates.push(parsed);
          }
        };

        const artistResponse = await get<SpotifyArtist>(`/spotify/artist/${spotifyArtistId}`);
        if (cancelled) return;
        setArtist(artistResponse);
        if (artistResponse?.source === "cache") {
          cacheSources.push("artist metrics");
          recordSnapshot(artistResponse.snapshot_at);
        }

        try {
          const historyResponse = await get<{ history?: PopularitySnapshot[] }>(
            `/spotify/artist/${spotifyArtistId}/popularity-history`,
            { params: { limit: 36 } },
          );
          if (cancelled) return;
          const history = historyResponse.history ?? [];
          setPopularityHistory(history);
          if (history.some((entry) => entry.source === "cache")) {
            cacheSources.push("popularity history");
            history.forEach((entry) => recordSnapshot(entry.snapshot_at));
          }
        } catch {
          if (!cancelled) {
            setPopularityHistory([]);
          }
        }

        try {
          const albumsResponse = await get<{ items?: SpotifyAlbum[] }>(
            `/spotify/artist/${spotifyArtistId}/albums`,
            { params: { limit: 6 } },
          );
          if (cancelled) return;
          setArtistAlbums(albumsResponse.items ?? []);
        } catch {
          if (!cancelled) {
            setArtistAlbums([]);
          }
        }

        try {
          const relatedResponse = await get<{ artists?: RelatedArtist[] }>(
            `/spotify/artist/${spotifyArtistId}/related-artists`,
          );
          if (cancelled) return;
          setRelatedArtists(relatedResponse.artists ?? []);
        } catch {
          if (!cancelled) {
            setRelatedArtists([]);
          }
        }

        const topTracksResponse = await get<{ tracks?: SpotifyTrack[] }>(
          `/spotify/artist/${spotifyArtistId}/top-tracks`,
        );
        if (cancelled) return;
        const tracks = topTracksResponse?.tracks ?? [];
        setTopTracks(tracks);
        if (tracks.some((track) => track.source === "cache")) {
          cacheSources.push("top tracks");
          tracks.forEach((track) => recordSnapshot(track.snapshot_at));
        }

        if (cacheSources.length) {
          setCacheNotice(generateCacheNotice(cacheSources, snapshotDates));
        } else {
          setCacheNotice(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load Spotify analytics right now.");
          setCacheNotice(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSpotifyAnalytics();
    return () => {
      cancelled = true;
    };
  }, [spotifyArtistId]);

  const displayHistory = useMemo(() => {
    if (popularityHistory.length) {
      return popularityHistory;
    }
    if (!artist) {
      return [];
    }
    const fallbackSnapshot = artist.snapshot_at ?? new Date().toISOString();
    return [
      {
        snapshot_at: fallbackSnapshot,
        popularity: artist.popularity,
        followers: artist.followers?.total,
        source: artist.source ?? "live",
      },
    ];
  }, [artist, popularityHistory]);

  const hasHistory = popularityHistory.length > 0;
  const isUsingFallbackHistory = !hasHistory && displayHistory.length > 0;

  const popularityTrend = useMemo(() => {
    return displayHistory.map((snapshot) => ({
      label: formatTrendLabel(snapshot.snapshot_at),
      value: snapshot.popularity ?? 0,
    }));
  }, [displayHistory]);

  const sortedAlbums = useMemo(() => {
    return [...artistAlbums].sort((a, b) => {
      const aDate = parseSnapshot(a.release_date);
      const bDate = parseSnapshot(b.release_date);
      if (aDate && bDate) {
        return bDate.getTime() - aDate.getTime();
      }
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  }, [artistAlbums]);

  const topGenre = useMemo(() => {
    if (!artist?.genres?.length) return null;
    return artist.genres[0]
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [artist]);

  const formatFollowers = () => {
    if (artist?.followers?.total) {
      return compactNumberFormatter.format(artist.followers.total);
    }
    return loading ? "Loading…" : "—";
  };

  const formatPopularity = () => {
    if (typeof artist?.popularity === "number") {
      return `${artist.popularity}`;
    }
    return loading ? "Loading…" : "—";
  };

  return (
    <section className="aa-page-shell space-y-6">
      <header className="flex flex-col gap-2">
        <p className="text-sm uppercase tracking-widest text-primary/80">Spotify Analytics</p>
        <h1 className="aa-page-title">Music Intelligence Dashboard</h1>
        <p className="text-muted-foreground">
          Monitor follower growth, track energy, and audio feature distribution for tracked Americana artists.
        </p>
      </header>

      {artistSelectionNotice && (
        <div className="rounded-md border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary">
          {artistSelectionNotice}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-900/20 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {cacheNotice && (
        <div className="rounded-md border border-sky-500/30 bg-sky-900/10 px-4 py-3 text-sm text-sky-100">
          {cacheNotice}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Followers"
          value={formatFollowers()}
          description={artist ? `${artist.name} on Spotify` : displayArtistName}
        />
        <MetricCard
          title="Popularity"
          value={formatPopularity()}
          description="Spotify popularity score"
        />
        <MetricCard
          title="Last Sync"
          value={syncState?.timestamp ? new Date(syncState.timestamp).toLocaleString() : "Pending"}
          description={`Status: ${syncState?.status ?? "idle"}`}
        />
        <MetricCard title="Top Genre" value={topGenre ?? (loading ? "Loading…" : "—")} description="Spotify tags" />
      </div>

      <TrendChart
        title="Artist Popularity Trend"
        data={popularityTrend}
        color="#38bdf8"
      />

      <Card className="aa-glass-card">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">{displayArtistName} Insights</CardTitle>
          <p className="text-sm text-muted-foreground">More Spotify feeds to expand the Artists home page</p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
                <span>Latest releases</span>
                {sortedAlbums.length ? (
                  <span className="text-sky-300">{sortedAlbums.length} tracked</span>
                ) : null}
              </div>
              {sortedAlbums.length ? (
                <div className="space-y-2">
                  {sortedAlbums.slice(0, 5).map((album, index) => {
                    const albumKey = album.id ?? `${album.name}-${index}`;
                    const albumLink = album.external_urls?.spotify;
                    const releaseDate = formatReleaseDate(album.release_date ?? "");
                    const trackCount = album.total_tracks ?? "—";
                    const albumType = album.album_type ?? "album";
                    const label = album.label ?? "Independent";
                    const markets = album.available_markets?.length ? `${album.available_markets.length} markets` : "Global";
                    return (
                      <div
                        key={albumKey}
                        className="flex items-start justify-between gap-3 rounded border border-border/60 bg-background/30 px-4 py-3 text-sm text-foreground/80 transition hover:border-primary/60"
                      >
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <p className="font-medium text-foreground">{album.name}</p>
                              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                {releaseDate} · {trackCount} tracks · {albumType}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <span>Label: {label}</span>
                            <span>Precision: {formatReleasePrecision(album.release_date_precision)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Spotify link</span>
                            {albumLink ? (
                              <a
                                href={albumLink}
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-sky-300 hover:text-sky-100"
                              >
                                Open on Spotify ↗
                              </a>
                            ) : (
                              <span className="text-muted-foreground">Unavailable</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs uppercase tracking-wide text-muted-foreground">
                          <p className="text-primary">{markets}</p>
                          <p>Markets</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Latest releases will appear here once the artist syncs.</p>
              )}
            </div>
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Related artists</div>
              {relatedArtists.length ? (
                <div className="space-y-2">
                  {relatedArtists.slice(0, 6).map((related) => (
                    <div
                      key={related.id}
                      className="space-y-2 rounded border border-border/60 bg-background/30 px-3 py-3 text-sm text-foreground/80"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 space-y-1">
                          <p className="font-medium text-foreground">{related.name}</p>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {related.genres?.slice(0, 2).join(", ") || "Genre unknown"}
                          </p>
                        </div>
                        <div className="text-right text-xs uppercase tracking-wide text-muted-foreground">
                          <p className="font-semibold text-primary">
                            {related.popularity ?? "—"}
                          </p>
                          <p>Popularity</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Spotify suggests this act</span>
                        <a
                          href={`https://open.spotify.com/artist/${related.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-sky-300 hover:text-sky-100"
                        >
                          Open on Spotify ↗
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Related artists populate when Spotify returns a match for this artist.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="aa-glass-card">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">{displayArtistName} Popularity Timeline</CardTitle>
          <p className="text-sm text-muted-foreground">
            {hasHistory
              ? "Spotify popularity score history (cached snapshots)"
              : "Latest Spotify popularity snapshot (history builds after the next sync)."}
          </p>
        </CardHeader>
        <CardContent>
          {displayHistory.length ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[2fr,1fr,1fr] gap-4 px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>Snapshot</span>
                <span className="text-right">Popularity</span>
                <span className="text-right">Followers</span>
              </div>
              <div className="divide-y divide-gray-800 border border-border/40 rounded-lg bg-background/40">
                {displayHistory.map((snapshot, index) => (
                  <div
                    key={`${snapshot.snapshot_at ?? index}-${index}`}
                    className="grid grid-cols-[2fr,1fr,1fr] gap-4 px-4 py-3 text-sm text-foreground"
                  >
                    <span>{formatLongSnapshot(snapshot.snapshot_at)}</span>
                    <span className="text-right font-semibold text-primary">
                      {snapshot.popularity ?? "—"}
                    </span>
                    <span className="text-right text-muted-foreground">
                      {formatFollowerCount(snapshot.followers)}
                    </span>
                  </div>
                ))}
              </div>
              {isUsingFallbackHistory && displayHistory[0] && (
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Showing the most recent snapshot (
                  {formatLongSnapshot(displayHistory[0].snapshot_at)}) until we capture a longer history.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {loading
                ? "Loading popularity history…"
                : `Popularity history will appear here once ${displayArtistName} is synced.`}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="aa-glass-card">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">{displayArtistName} Top Tracks</CardTitle>
          <p className="text-sm text-muted-foreground">Click a row to open each track’s Spotify endpoints</p>
        </CardHeader>
        <CardContent>
          {topTracks.length ? (
            <div className="space-y-2">
              {topTracks.slice(0, 8).map((track, index) => {
                const release = track.album?.release_date ? formatReleaseDate(track.album.release_date) : "—";
                const trackId = track.id;
                const trackLink = trackId ? `/marketing/music-analytics/track/${encodeURIComponent(trackId)}` : undefined;
                const metadataState = { trackName: track.name, artistName: displayArtistName };
                return (
                  <div
                    key={`${trackId ?? `track-${index}`}-${index}`}
                    className="flex items-center justify-between gap-3 rounded border border-border/60 bg-background/30 px-4 py-3 text-sm text-foreground/80 transition hover:border-primary/60"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-primary/80">{index + 1}.</span>
                      <div>
                        {trackLink ? (
                          <Link to={trackLink} state={metadataState} className="font-medium text-foreground hover:text-foreground">
                            {track.name}
                          </Link>
                        ) : (
                          <p className="font-medium text-foreground">{track.name}</p>
                        )}
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{release}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs uppercase tracking-wide text-muted-foreground">
                      <p className="text-primary">{track.popularity ?? "—"}</p>
                      <p>Popularity</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {loading ? "Loading top tracks…" : `No Spotify tracks available for ${displayArtistName}.`}
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function formatTrendLabel(snapshot?: string): string {
  const date = parseSnapshot(snapshot);
  if (date) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return snapshot ?? "—";
}

function formatLongSnapshot(snapshot?: string): string {
  const date = parseSnapshot(snapshot);
  if (!date) {
    return snapshot ?? "—";
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatFollowerCount(value?: number): string {
  if (typeof value === "number") {
    return compactNumberFormatter.format(value);
  }
  return "—";
}

function formatReleaseDate(input?: string): string {
  if (!input) return "—";
  const trimmed = input.trim();
  if (!trimmed) return "—";
  const parts = trimmed.split("-");
  if (parts.length === 1 && /^\d{4}$/.test(parts[0])) {
    return parts[0];
  }
  if (
    parts.length === 2
    && /^\d{4}$/.test(parts[0])
    && /^\d{2}$/.test(parts[1])
  ) {
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    if (!Number.isNaN(year) && !Number.isNaN(month)) {
      const date = new Date(Date.UTC(year, month - 1, 1));
      return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    }
  }
  if (
    parts.length === 3
    && /^\d{4}$/.test(parts[0])
    && /^\d{2}$/.test(parts[1])
    && /^\d{2}$/.test(parts[2])
  ) {
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    }
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  return trimmed;
}

function formatReleasePrecision(precision?: string): string {
  if (!precision) return "Unknown";
  const normalized = precision.toLowerCase();
  if (normalized === "day") return "Day";
  if (normalized === "month") return "Month";
  if (normalized === "year") return "Year";
  return precision.charAt(0).toUpperCase() + precision.slice(1);
}

function parseSnapshot(snapshot?: string): Date | null {
  if (!snapshot) return null;
  const parsed = new Date(snapshot);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function generateCacheNotice(sources: string[], snapshots: Date[]): string {
  const uniqueSources = Array.from(new Set(sources));
  if (!uniqueSources.length) {
    return "";
  }
  const described = formatSourceList(uniqueSources);
  const latestSnapshot = snapshots.length
    ? new Date(Math.max(...snapshots.map((date) => date.getTime())))
    : null;
  const snapshotLabel = latestSnapshot ? ` captured ${latestSnapshot.toLocaleString()}` : "";
  return `Showing cached Spotify ${described} data${snapshotLabel}. Trigger a sync to refresh when possible.`;
}

function formatSourceList(entries: string[]): string {
  if (entries.length <= 1) return entries[0] ?? "";
  if (entries.length === 2) return `${entries[0]} and ${entries[1]}`;
  return `${entries.slice(0, -1).join(", ")}, and ${entries[entries.length - 1]}`;
}
